import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import { PantallaConfiguracion } from '@/app/PantallaConfiguracion'
import { Rutas } from '@/app/rutas'
import { configuracionCompleta } from '@/lib/entorno'
import { iniciarActualizaciones } from '@/lib/nativo/actualizacion'
import { ProveedorSesion } from '@/modules/auth/ProveedorSesion'

import '@/styles/index.css'

/**
 * Punto de entrada.
 *
 * El orden de los proveedores no es casual: el router va por fuera porque la
 * pantalla de ingreso necesita redirigir, y la sesión por dentro del cliente
 * de consultas para que al salir se limpie lo que quedó en memoria.
 */
const clienteConsultas = new QueryClient({
  defaultOptions: {
    queries: {
      // Los datos de la matriz cambian cuando alguien los cambia, no solos.
      // Medio minuto evita recargar en cada cambio de pestaña sin dejar la
      // pantalla desactualizada.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Un error de permiso o de validación no mejora reintentando: solo hace
      // esperar al usuario para darle el mismo error.
      retry: 1,
      /*
       * SIN RED, QUE CORRA IGUAL Y DECIDA QUIEN SABE.
       *
       * Por defecto React Query no ejecuta nada cuando el navegador se
       * declara sin conexión: deja la consulta «en pausa», callada. Para una
       * aplicación de oficina da igual; para la del técnico es el fallo
       * entero, porque en la obra NO HAY SEÑAL y lo que hay que hacer es
       * trabajar con lo guardado.
       */
      networkMode: 'offlineFirst',
    },
    mutations: {
      /*
       * Y LO MISMO CON LAS MUTACIONES, que es donde dolía de verdad.
       *
       * Una mutación pausada ni corre ni falla: se queda viva para siempre.
       * Eso dejaba el botón «Llegué» en «Tomando ubicación…» sin fin —medido:
       * treinta segundos sin que la petición de GPS llegara siquiera a
       * dispararse—, y el técnico mirando el teléfono sin saber si marcó.
       *
       * Afectaba a TODO lo que el técnico hace: llegada, salida, avances,
       * guardar la cotización. Con esto, cada una decide qué hacer sin red:
       * guardarse en el teléfono o fallar diciendo por qué. Callarse no es
       * una opción.
       */
      networkMode: 'offlineFirst',
    },
  },
})

/*
 * EL SERVICE WORKER SE REGISTRA SIEMPRE.
 *
 * Antes solo se registraba al pedir permiso de notificaciones, porque solo
 * servía para eso. Ahora también es lo que permite ABRIR la aplicación sin
 * señal, y eso no puede depender de que alguien haya dicho que sí a los
 * avisos: el técnico que los rechazó es el mismo que trabaja en la obra sin
 * cobertura.
 */
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {
      // Sin worker la aplicación funciona igual con señal: no se avisa de
      // algo que la persona no puede arreglar.
    })
  })
}

// En la APK: confirma que esta versión arrancó y busca una más nueva. En la
// web no hace nada; ahí cada recarga ya trae lo último.
iniciarActualizaciones()

const contenedor = document.getElementById('root')

if (!contenedor) {
  throw new Error('No se encontró el elemento #root en index.html.')
}

// Sin llaves de Supabase no se monta nada más: la sesión intentaría consultar
// contra una dirección inventada y el usuario vería una sucesión de errores en
// vez de la única instrucción que necesita.
createRoot(contenedor).render(
  <StrictMode>
    {configuracionCompleta ? (
      <QueryClientProvider client={clienteConsultas}>
        <ProveedorSesion>
          <BrowserRouter>
            <Rutas />
          </BrowserRouter>
        </ProveedorSesion>
      </QueryClientProvider>
    ) : (
      <PantallaConfiguracion />
    )}
  </StrictMode>,
)
